import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Filter, ChevronDown, ChevronUp, X, Save, Bookmark } from 'lucide-react';
import { SOURCE_OPTIONS } from './constants';

export function LeadsFilters({
  filterSource, setFilterSource, filterScore, setFilterScore,
  filterDateFrom, setFilterDateFrom, filterDateTo, setFilterDateTo,
  filterTag, setFilterTag, filterSharedWithMe, setFilterSharedWithMe,
  filterMinEmployees, setFilterMinEmployees, filterMaxEmployees, setFilterMaxEmployees,
  filterMinRevenue, setFilterMinRevenue, filterMaxRevenue, setFilterMaxRevenue,
  filterVerifiedEmail, setFilterVerifiedEmail,
  activeFilterCount, clearAllFilters, leads,
  savedFilters, applyFilter, handleDeleteSavedFilter,
  showSaveFilter, setShowSaveFilter, filterName, setFilterName, handleSaveFilter,
  showAdvanced, setShowAdvanced,
}) {
  return (
    <>
      <Button variant="outline" className={`rounded-sm text-xs h-9 gap-1 ${showAdvanced ? 'bg-[#EFF6FF] border-[#0047AB]/20 text-[#0047AB]' : ''}`} onClick={() => setShowAdvanced(!showAdvanced)} data-testid="toggle-advanced-filters">
        <Filter className="w-3.5 h-3.5" /> Filters {activeFilterCount > 0 && <span className="w-4 h-4 bg-[#0047AB] text-white rounded-full text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
        {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </Button>
      {showAdvanced && (
        <div className="border border-slate-200 rounded-sm bg-white p-4" data-testid="advanced-filters-panel">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Source</label>
              <Select value={filterSource || 'all'} onValueChange={v => setFilterSource(v === 'all' ? '' : v)}>
                <SelectTrigger className="mt-1 rounded-sm text-xs h-8" data-testid="filter-source"><SelectValue placeholder="All Sources" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Sources</SelectItem>{SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Score</label>
              <Select value={filterScore || 'all'} onValueChange={v => setFilterScore(v === 'all' ? '' : v)}>
                <SelectTrigger className="mt-1 rounded-sm text-xs h-8" data-testid="filter-score"><SelectValue placeholder="All Scores" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Scores</SelectItem><SelectItem value="hot">Hot (40+)</SelectItem><SelectItem value="warm">Warm (15-39)</SelectItem><SelectItem value="cold">Cold (&lt;15)</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">From Date</label>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="mt-1 rounded-sm text-xs h-8" data-testid="filter-date-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">To Date</label>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="mt-1 rounded-sm text-xs h-8" data-testid="filter-date-to" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Tag</label>
              <Input value={filterTag} onChange={e => setFilterTag(e.target.value)} placeholder="Filter by tag" className="mt-1 rounded-sm text-xs h-8" data-testid="filter-tag" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Min Employees</label>
              <Input type="number" min="0" value={filterMinEmployees} onChange={e => setFilterMinEmployees(e.target.value)} placeholder="e.g. 10" className="mt-1 rounded-sm text-xs h-8" data-testid="filter-min-employees" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Max Employees</label>
              <Input type="number" min="0" value={filterMaxEmployees} onChange={e => setFilterMaxEmployees(e.target.value)} placeholder="e.g. 500" className="mt-1 rounded-sm text-xs h-8" data-testid="filter-max-employees" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Min Revenue ($)</label>
              <Input type="number" min="0" value={filterMinRevenue} onChange={e => setFilterMinRevenue(e.target.value)} placeholder="e.g. 100000" className="mt-1 rounded-sm text-xs h-8" data-testid="filter-min-revenue" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Max Revenue ($)</label>
              <Input type="number" min="0" value={filterMaxRevenue} onChange={e => setFilterMaxRevenue(e.target.value)} placeholder="e.g. 5000000" className="mt-1 rounded-sm text-xs h-8" data-testid="filter-max-revenue" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Hunter Verified</label>
              <Select value={filterVerifiedEmail || 'all'} onValueChange={v => setFilterVerifiedEmail(v === 'all' ? '' : v)}>
                <SelectTrigger className="mt-1 rounded-sm text-xs h-8" data-testid="filter-verified-email"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="valid">Verified deliverable</SelectItem>
                  <SelectItem value="invalid">Verified undeliverable</SelectItem>
                  <SelectItem value="any">Has any verification</SelectItem>
                  <SelectItem value="none">Not yet verified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 cursor-pointer h-8">
                <input type="checkbox" checked={filterSharedWithMe} onChange={e => setFilterSharedWithMe(e.target.checked)} className="rounded-sm" data-testid="filter-shared-with-me" />
                <span className="text-xs text-slate-700">Shared with me</span>
              </label>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={clearAllFilters} data-testid="clear-filters-btn"><X className="w-3 h-3 mr-1" /> Clear All</Button>}
              <span className="text-[10px] text-slate-400">{leads.length} results</span>
            </div>
            <div className="flex items-center gap-2">
              {savedFilters.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" data-testid="load-filter-btn"><Bookmark className="w-3 h-3 mr-1" /> Saved ({savedFilters.length})</Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {savedFilters.map(sf => (
                      <DropdownMenuItem key={sf.id} className="flex items-center justify-between" data-testid={`saved-filter-${sf.id}`}>
                        <span onClick={() => applyFilter(sf)} className="flex-1 cursor-pointer">{sf.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteSavedFilter(sf.id); }} className="ml-2 text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {activeFilterCount > 0 && (
                showSaveFilter ? (
                  <div className="flex items-center gap-1">
                    <Input value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Filter name..." className="h-7 w-32 text-xs rounded-sm" data-testid="save-filter-name" />
                    <Button size="sm" className="h-7 text-xs bg-slate-900 text-white rounded-sm" onClick={handleSaveFilter} data-testid="save-filter-confirm"><Save className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowSaveFilter(false)}><X className="w-3 h-3" /></Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => setShowSaveFilter(true)} data-testid="save-filter-btn"><Save className="w-3 h-3 mr-1" /> Save Filter</Button>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
